/**
 * @module Inline chip nodes for the Tiptap composer.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), rendering Forge's chip
 * vocabulary: `@path` file mentions, `$name` skills, and terminal-context
 * placeholder chips.
 */

import { Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from "@tiptap/react";
import type { ServerProviderSkill } from "@cmmd-forge/contracts";
import { createContext, use } from "react";

import type { TerminalContextDraft } from "~/lib/terminalContext";
import { getVscodeIconUrlForEntry, inferEntryKindFromPath } from "~/vscode-icons";
import { basenameOfPath } from "~/filePath";
import {
  COMPOSER_INLINE_CHIP_CLASS_NAME,
  COMPOSER_INLINE_CHIP_ICON_CLASS_NAME,
  COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME,
  COMPOSER_INLINE_SKILL_CHIP_CLASS_NAME,
  SKILL_CHIP_ICON_SVG,
} from "./composerInlineChip";
import { ComposerPendingTerminalContextChip } from "./chat/ComposerPendingTerminalContexts";
import { formatProviderSkillDisplayName } from "~/providerSkillPresentation";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

/**
 * Wraps an inline chip node view: keeps the caret and text selection out of
 * the chip and paints the editor's node selection over it.
 */
const COMPOSER_CHIP_NODE_SELECTION_CLASS_NAME =
  "relative inline-flex select-none items-center align-middle leading-none data-[composer-chip-selected]:after:pointer-events-none data-[composer-chip-selected]:after:absolute data-[composer-chip-selected]:after:inset-0 data-[composer-chip-selected]:after:rounded-sm data-[composer-chip-selected]:after:bg-[Highlight] data-[composer-chip-selected]:after:opacity-30 data-[composer-chip-selected]:after:content-['']";

export const RichComposerSkillsContext = createContext<ReadonlyArray<ServerProviderSkill>>([]);

function resolvedThemeFromDocument(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

function MentionChipContent({ path }: { readonly path: string }) {
  return (
    <>
      <img
        alt=""
        aria-hidden="true"
        className={COMPOSER_INLINE_CHIP_ICON_CLASS_NAME}
        loading="lazy"
        src={getVscodeIconUrlForEntry(
          path,
          inferEntryKindFromPath(path),
          resolvedThemeFromDocument(),
        )}
      />
      <span className={COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME}>{basenameOfPath(path)}</span>
    </>
  );
}

function ComposerMentionNodeView({ node }: NodeViewProps) {
  const path = (node.attrs.path as string) ?? "";
  return (
    <NodeViewWrapper
      as="span"
      className={COMPOSER_CHIP_NODE_SELECTION_CLASS_NAME}
      contentEditable={false}
      spellCheck={false}
      data-composer-mention-chip="true"
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <span className={COMPOSER_INLINE_CHIP_CLASS_NAME}>
              <MentionChipContent path={path} />
            </span>
          }
        />
        <TooltipPopup side="top">{path}</TooltipPopup>
      </Tooltip>
    </NodeViewWrapper>
  );
}

export const ComposerMentionExtension = Node.create({
  name: "composer-mention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      path: { default: "" },
      source: { default: "" },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-composer-mention]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", { "data-composer-mention": "", ...HTMLAttributes }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ComposerMentionNodeView);
  },
});

function resolveSkillDescription(
  skill: Pick<ServerProviderSkill, "shortDescription" | "description">,
): string | null {
  const shortDescription = skill.shortDescription?.trim();
  if (shortDescription) {
    return shortDescription;
  }
  return skill.description?.trim() || null;
}

function ComposerSkillNodeView({ node }: NodeViewProps) {
  const skills = use(RichComposerSkillsContext);
  const skillName = (node.attrs.skillName as string) ?? "";
  const skillLabel = (node.attrs.skillLabel as string) || skillName;
  const skillDescription = (node.attrs.skillDescription as string | null) ?? null;
  const skill = skills.find((candidate) => candidate.name === skillName);
  const description = skill ? resolveSkillDescription(skill) : skillDescription;
  const chip = (
    <span
      className={COMPOSER_INLINE_SKILL_CHIP_CLASS_NAME}
      contentEditable={false}
      spellCheck={false}
      data-composer-skill-chip="true"
    >
      <span
        aria-hidden="true"
        className={COMPOSER_INLINE_CHIP_ICON_CLASS_NAME}
        dangerouslySetInnerHTML={{ __html: SKILL_CHIP_ICON_SVG }}
      />
      <span className={COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME}>{skillLabel}</span>
    </span>
  );
  return (
    <NodeViewWrapper as="span" className={COMPOSER_CHIP_NODE_SELECTION_CLASS_NAME}>
      {!description ? (
        chip
      ) : (
        <Tooltip>
          <TooltipTrigger render={chip} />
          <TooltipPopup side="top" className="max-w-[30rem] whitespace-normal leading-tight">
            {description}
          </TooltipPopup>
        </Tooltip>
      )}
    </NodeViewWrapper>
  );
}

export const ComposerSkillExtension = Node.create({
  name: "composer-skill",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      skillName: { default: "" },
      skillLabel: { default: "" },
      skillDescription: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-composer-skill]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", { "data-composer-skill": "", ...HTMLAttributes }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ComposerSkillNodeView);
  },
});

function ComposerTerminalContextNodeView({ node }: NodeViewProps) {
  const context = node.attrs.context as TerminalContextDraft | null;
  return (
    <NodeViewWrapper
      as="span"
      className={COMPOSER_CHIP_NODE_SELECTION_CLASS_NAME}
      contentEditable={false}
      spellCheck={false}
      data-composer-terminal-context-chip="true"
    >
      {context ? <ComposerPendingTerminalContextChip context={context} /> : null}
    </NodeViewWrapper>
  );
}

const REQUIRED_TERMINAL_CONTEXT_STRINGS = [
  "id",
  "threadId",
  "createdAt",
  "terminalId",
  "terminalLabel",
  "text",
] as const;
const REQUIRED_TERMINAL_CONTEXT_NUMBERS = ["lineStart", "lineEnd"] as const;

/** Validates pasted terminal context fields before the chip reads them for display and expiry. */
function usableTerminalContextDraft(parsed: unknown): TerminalContextDraft | null {
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  const value = parsed as Record<string, unknown>;
  const hasStrings = REQUIRED_TERMINAL_CONTEXT_STRINGS.every(
    (field) => typeof value[field] === "string",
  );
  const hasNumbers = REQUIRED_TERMINAL_CONTEXT_NUMBERS.every(
    (field) => typeof value[field] === "number",
  );
  return hasStrings && hasNumbers ? (parsed as TerminalContextDraft) : null;
}

export const ComposerTerminalContextExtension = Node.create({
  name: "composer-terminal-context",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  /** Defines chip attributes and preserves structured data across HTML clipboard round trips. */
  addAttributes() {
    return {
      // HTML-only clipboards paste through the schema's HTML parsing, so the
      // object attribute must survive a string round trip; an undecodable
      // value degrades to null and the node view renders nothing.
      context: {
        default: null,
        /** Decodes pasted context data, returning null for absent or malformed values. */
        parseHTML: (element) => {
          const value = element.getAttribute("context");
          if (!value) return null;
          try {
            const parsed: unknown = JSON.parse(value);
            return usableTerminalContextDraft(parsed);
          } catch {
            return null;
          }
        },
        /** Serializes a present context as JSON for HTML clipboard export. */
        renderHTML: (attributes) =>
          attributes.context == null ? {} : { context: JSON.stringify(attributes.context) },
      },
      source: { default: "" },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-composer-terminal-context]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", { "data-composer-terminal-context": "", ...HTMLAttributes }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ComposerTerminalContextNodeView);
  },
});

/** Skill label and description lookup for the document model builder. */
export function resolveSkillMeta(
  skills: ReadonlyArray<ServerProviderSkill>,
  nameInput: string,
): { label: string; description: string | null } {
  const normalized = nameInput.startsWith("$") ? nameInput.slice(1) : nameInput;
  const found = skills.find((candidate) => candidate.name === normalized);
  if (!found) {
    return {
      label: formatProviderSkillDisplayName({ name: normalized }),
      description: null,
    };
  }
  return {
    label: formatProviderSkillDisplayName(found),
    description: resolveSkillDescription(found),
  };
}
